import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type MaventaEnvironment = 'stage' | 'production';

export interface MaventaLastTest {
  testedAt: string;
  success: boolean;
  error?: string;
  companyName?: string;
  companyBid?: string;
  /** Maventa KYC state; only VERIFIED may send. */
  companyState?: string;
  authState?: string;
  missingScopes?: string[];
}

export interface MaventaCredentialsStatus {
  companyId: string | null;
  /** Environment the credentials were saved for. */
  environment: MaventaEnvironment | null;
  hasSecret: boolean;
  encryptionAvailable: boolean;
  lastTest: MaventaLastTest | null;
  /** True only for a VERIFIED company tested in the platform's environment. */
  enabled: boolean;
}

export interface MaventaIntegrationResponse {
  /** False when this environment has no Maventa vendor key. */
  available: boolean;
  environment: MaventaEnvironment | null;
  credentials: MaventaCredentialsStatus;
}

export interface MaventaCredentialsInput {
  companyId: string;
  /** Omit to keep the stored key. */
  apiKey?: string;
}

export interface MaventaTestResponse {
  success: boolean;
  credentials: MaventaCredentialsStatus;
}

const MAVENTA_QUERY_KEY = ['integrations', 'maventa'] as const;

export function useMaventaIntegration(enabled: boolean) {
  return useQuery({
    queryKey: MAVENTA_QUERY_KEY,
    queryFn: () => api.get<MaventaIntegrationResponse>('/integrations/maventa'),
    enabled,
  });
}

export function useSaveMaventaCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MaventaCredentialsInput) =>
      api.put<MaventaCredentialsStatus>('/integrations/maventa/credentials', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MAVENTA_QUERY_KEY });
    },
  });
}

export function useTestMaventaCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<MaventaTestResponse>('/integrations/maventa/test', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MAVENTA_QUERY_KEY });
    },
  });
}

export function useDeleteMaventaCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<MaventaCredentialsStatus>('/integrations/maventa/credentials'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MAVENTA_QUERY_KEY });
    },
  });
}

/** Where an e-invoice to a company would go (#3071). */
export interface MaventaReceiversResponse {
  /** Whether this workshop can send e-invoices at all. */
  ready: boolean;
  /** The company's stored address, else the OVT from its business id. */
  target: { eia: string; operator: string | null; source: 'stored' | 'business_id' } | null;
  /** Maventa's registry entries for the company. */
  receivers: { eia: string; operator: string; network: string }[];
  /** The registry could not be asked; the target still stands. */
  lookupFailed: boolean;
}

/**
 * E-invoice readiness and the recipient's address for the send dialogs.
 * Needs only `invoices.manage`; without a company it answers readiness only.
 */
export function useMaventaReceivers(companyId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...MAVENTA_QUERY_KEY, 'receivers', companyId ?? null],
    queryFn: () =>
      api.get<MaventaReceiversResponse>(
        companyId
          ? `/integrations/maventa/receivers?companyId=${encodeURIComponent(companyId)}`
          : '/integrations/maventa/receivers',
      ),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export interface EinvoiceChoice {
  /** Show the option at all: the workshop can send e-invoices. */
  visible: boolean;
  /** The recipient can be reached; otherwise the option is shown disabled. */
  available: boolean;
  /** Pre-select it: the address is stored or confirmed by the registry. */
  preferred: boolean;
  /** Address to send to when it differs from the recipient's own. */
  pick: { eia: string; operator: string } | null;
}

/**
 * Decide the e-invoice option from a receivers answer (#3071). An address
 * stored on the company is what the workshop entered and is always used. An
 * OVT derived from the business id is only a guess: when the registry lists
 * the company under another address, the first registry entry is used — it
 * is where Maventa says the company receives. A derived OVT the registry does
 * not know is no address at all, unless the registry could not be asked.
 */
export function einvoiceChoice(data: MaventaReceiversResponse | undefined): EinvoiceChoice {
  if (!data?.ready) return { visible: false, available: false, preferred: false, pick: null };
  const { target, receivers, lookupFailed } = data;
  const stored = target?.source === 'stored';
  const matching = target ? receivers.find((receiver) => receiver.eia === target.eia) : undefined;
  const first = receivers[0];
  const pick = stored || matching || !first ? null : { eia: first.eia, operator: first.operator };
  const available = receivers.length > 0 || (target !== null && (stored || lookupFailed));
  return { visible: true, available, preferred: available && (stored || receivers.length > 0), pick };
}
